/**
 * PriceTicker – Bloomberg/CNBC-style scrolling price bar.
 * Sits at the very top of the authenticated layout.
 * Refreshes from /api/markets/overview every 60 seconds.
 * Pauses on hover.
 */
import { useEffect, useRef, useState, useCallback } from 'react'
import { getMarketsOverview } from '@/lib/api'
import type { MarketsOverviewData } from '@/lib/types'

interface TickerItem {
  label: string
  price: string
  // null when the upstream quote carried no change field (backend emits
  // change/change_pct as null rather than inventing a zero)
  changePct: number | null
}

// ── Fallback / seed data shown before the first fetch resolves ───────────

const SEED_ITEMS: TickerItem[] = [
  { label: 'SPX',      price: '5,234',   changePct:  0.4  },
  { label: 'IXIC',     price: '16,400',  changePct:  0.6  },
  { label: 'FTSE',     price: '6,380',   changePct: -0.2  },
  { label: 'DAX',      price: '18,200',  changePct:  0.1  },
  { label: 'NKY',      price: '38,500',  changePct:  0.3  },
  { label: 'BTC',      price: '$67,000', changePct:  1.2  },
  { label: 'Gold',     price: '$2,340',  changePct: -0.3  },
  { label: 'GBP/USD',  price: '1.2650',  changePct:  0.1  },
]

// ── Derive ticker items from live API data ───────────────────────────────

function deriveItems(d: MarketsOverviewData): TickerItem[] {
  const items: TickerItem[] = []

  // Indices – show key ones first
  const indexPriority = ['SPX', 'NDX', 'INDU', 'UKX', 'DAX', 'CAC', 'NKY', 'HSI']
  const sortedIndices = [...d.indices].sort((a, b) => {
    const ai = indexPriority.indexOf(a.short)
    const bi = indexPriority.indexOf(b.short)
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi)
  })
  for (const idx of sortedIndices.slice(0, 6)) {
    items.push({
      label: idx.short,
      price: idx.price.toLocaleString('en-US', { maximumFractionDigits: 0 }),
      changePct: idx.change_pct,
    })
  }

  // Crypto – BTC + ETH
  for (const c of d.crypto.slice(0, 2)) {
    items.push({
      label: c.code,
      price: '$' + c.price.toLocaleString('en-US', { maximumFractionDigits: 0 }),
      changePct: c.change_pct,
    })
  }

  // Commodities – Gold + Oil
  for (const c of d.commodities.slice(0, 2)) {
    items.push({
      label: c.name.split(' ')[0],
      price: '$' + c.price.toLocaleString('en-US', { maximumFractionDigits: 0 }),
      changePct: c.change_pct,
    })
  }

  // Forex – 2 pairs
  for (const f of d.forex.slice(0, 2)) {
    items.push({
      label: f.pair,
      price: f.rate.toFixed(4),
      changePct: f.change_pct,
    })
  }

  return items
}

// ── Component ────────────────────────────────────────────────────────────

type TickerSize = 'sm' | 'md' | 'lg'
const SIZE_KEY = 'av-ticker-size'

const SIZE_CONFIG: Record<TickerSize, { bar: string; text: string; px: string; nextLabel: string }> = {
  sm: { bar: 'h-7',  text: 'text-[11px]', px: 'px-3 sm:px-4', nextLabel: 'M' },
  md: { bar: 'h-9',  text: 'text-sm',     px: 'px-4',         nextLabel: 'L' },
  lg: { bar: 'h-12', text: 'text-base',   px: 'px-5',         nextLabel: 'S' },
}

function loadSize(): TickerSize {
  try {
    const s = localStorage.getItem(SIZE_KEY)
    if (s === 'sm' || s === 'md' || s === 'lg') return s
  } catch { /* ignore */ }
  return 'md'  // default bumped from implicit-sm to md so the bar is readable
}

export default function PriceTicker() {
  const [items, setItems] = useState<TickerItem[]>(SEED_ITEMS)
  const [paused, setPaused] = useState(false)
  const [size, setSize] = useState<TickerSize>(loadSize)
  const trackRef = useRef<HTMLDivElement>(null)

  const cycleSize = () => {
    const next: TickerSize = size === 'sm' ? 'md' : size === 'md' ? 'lg' : 'sm'
    setSize(next)
    try { localStorage.setItem(SIZE_KEY, next) } catch { /* ignore */ }
  }

  const cfg = SIZE_CONFIG[size]

  const fetchData = useCallback(async () => {
    try {
      const d = await getMarketsOverview()
      const derived = deriveItems(d)
      if (derived.length > 0) setItems(derived)
    } catch {
      // silently keep current items on error
    }
  }, [])

  // Fetch on mount, then every 60 s
  useEffect(() => {
    fetchData()
    const id = setInterval(fetchData, 60_000)
    return () => clearInterval(id)
  }, [fetchData])

  // Duplicate items so the scroll feels seamless
  const displayItems = [...items, ...items]

  return (
    <div
      className={`relative ${cfg.bar} overflow-hidden bg-[#0a0a0f] border-b border-white/5 flex items-center`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="Live market prices"
      role="marquee"
    >
      {/* Size-toggle — small button on the left */}
      <button
        type="button"
        onClick={cycleSize}
        title={`Ticker size: ${size.toUpperCase()} — click for ${cfg.nextLabel}`}
        aria-label="Cycle ticker size"
        className="absolute left-1 top-1/2 -translate-y-1/2 z-20
                   w-5 h-5 rounded bg-white/5 hover:bg-white/15 border border-white/10
                   text-[9px] font-bold text-white/60 hover:text-white transition"
      >
        {size.toUpperCase()[0]}
      </button>

      {/* Fade edges */}
      <div className="absolute left-0 top-0 h-full w-10 z-10 pointer-events-none"
           style={{ background: 'linear-gradient(to right, #0a0a0f, transparent)' }} />
      <div className="absolute right-0 top-0 h-full w-10 z-10 pointer-events-none"
           style={{ background: 'linear-gradient(to left, #0a0a0f, transparent)' }} />

      {/* Scrolling track */}
      <div
        ref={trackRef}
        className="flex items-center whitespace-nowrap pl-10"
        style={{
          animation: paused ? 'none' : 'ticker-scroll 60s linear infinite',
          willChange: 'transform',
        }}
      >
        {displayItems.map((item, i) => {
          const pct = item.changePct
          const positive = (pct ?? 0) >= 0
          return (
            <span key={i} className={`inline-flex items-center gap-2 ${cfg.px} ${cfg.text} font-mono`}>
              <span className="text-white/60 tracking-wide font-semibold">{item.label}</span>
              <span className="text-white/90">{item.price}</span>
              <span className={`font-semibold ${pct == null ? 'text-white/40' : positive ? 'text-emerald-400' : 'text-rose-400'}`}>
                {pct == null ? '—' : `${positive ? '+' : ''}${pct.toFixed(2)}%`}
              </span>
              <span className="text-white/20 ml-2">|</span>
            </span>
          )
        })}
      </div>

      <style>{`
        @keyframes ticker-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  )
}
